import { Drawer } from '@components/Drawer';
import { Dialog, Form } from '@servicetitan/design-system';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import sharedStrings from 'src/constants/strings';
import strings from '../strings';

import { Contact, ContactUpsertInput } from '@api/generated/graphql';
import { transformContacts } from '../transformContacts';
import { useEffect, useMemo, useState } from 'react';
import FormFooterButtons from '@components/Drawer/components/FormFooterButtons';
import { inboxLocators } from '@locators/inbox';
import ToastModule from '@modules/ToastModule';
import editContactsValidationSchema, {
  EditContactsValidationType,
} from '../validations';
import ContactMethodForm from '@components/forms/components/ContactMethodForm/ContactMethodForm';
import { useUpsertCustomerContactsMutation } from '../api/useUpsertCustomerContactsMutation';
import { useContactSupportButton } from '@lib/hooks/useContactSupportButton';

export type EditContactProps = {
  contacts: Contact[];
  tenantId: string;
  customerId: string;
  open: boolean;
  onClose: () => void;
  refetchCustomerContacts: () => void;
};

export type EditFormPrefix = 'editContacts';

export function EditContactsDrawer({
  tenantId,
  customerId,
  contacts,
  open,
  onClose,
  refetchCustomerContacts,
}: EditContactProps) {
  const [exitPromptOpened, setExitPromptOpened] = useState(false);

  const {
    upsertCustomerContactsMutation,
    loading,
    reset: resetUpsertMutation,
  } = useUpsertCustomerContactsMutation();
  const contactSupportProps = useContactSupportButton();

  // Applies the phone mask to non-email fields
  const originalContactsMasked = useMemo(
    () => transformContacts(contacts),
    [contacts],
  );

  const defaultFormValues = useMemo(
    () => ({
      editContacts: { contacts: originalContactsMasked },
    }),
    [originalContactsMasked],
  );

  const formMethods = useForm<EditContactsValidationType>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(editContactsValidationSchema),
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, defaultValues },
  } = formMethods;

  const fieldArray = useFieldArray({
    control,
    name: `editContacts.contacts`,
  });

  const { update, append, remove, fields } = fieldArray;

  const handleClose = (force?: boolean) => {
    if (force) {
      setExitPromptOpened(false);
      closeDrawer();
      reset(defaultValues);
    } else if (isDirty) {
      setExitPromptOpened(true);
    } else {
      closeDrawer();
    }
  };

  const closeDrawer = () => {
    onClose();
    reset({ editContacts: { contacts: originalContactsMasked } });
    resetUpsertMutation();
  };

  const handleSubmitSuccess = () => {
    closeDrawer();
    refetchCustomerContacts();
    resetUpsertMutation();
    ToastModule.showToast({
      status: 'success',
      title: strings.successToast.title,
    });
  };

  const handleSubmitFailure = () => {
    ToastModule.showToast({
      status: 'critical',
      title: strings.errorToast.title,
      toastBody: strings.errorToast.message,
      primaryActionName: 'Try Again',
      onPrimaryActionClick: handleSubmit(onSubmit),
      ...contactSupportProps,
    });
  };

  const onSubmit = async (data: EditContactsValidationType) => {
    // Compare the original contacts with the new contacts to see what has change

    if (!isDirty) {
      closeDrawer();
      return;
    }

    const contactsInput: ContactUpsertInput[] = data.editContacts.contacts.map(
      (contact) => ({
        ...contact,
        id: contact.id ? contact.id.toString() : undefined,
      }),
    );

    const result = await upsertCustomerContactsMutation({
      input: contactsInput,
      tenantId,
      customerId,
    });

    if (result.data?.upsertCustomerContact) {
      handleSubmitSuccess();
    } else {
      handleSubmitFailure();
    }
  };

  useEffect(() => {
    reset(defaultFormValues);
  }, [reset, defaultFormValues]);

  return (
    <Drawer
      onClose={() => handleClose()}
      open={open}
      header={strings.labels.header}
      footer={
        <FormFooterButtons
          primaryLocator={inboxLocators.customerSearch.submitSaveBtn}
          secondaryLocator={inboxLocators.customerSearch.submitCancelBtn}
          loading={loading}
          primaryText={sharedStrings.labels.editContacts.primaryAction}
          secondaryText={sharedStrings.labels.editContacts.secondaryAction}
          onSecondaryClick={handleClose}
          onPrimaryClick={handleSubmit(onSubmit)}
        />
      }
    >
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ContactMethodForm
            formFieldPrefix='editContacts'
            update={update}
            append={append}
            remove={remove}
            contactMethodFields={fields}
          />
        </Form>
      </FormProvider>
      <Dialog
        open={exitPromptOpened}
        focusTrapOptions={{ disabled: true }}
        title={sharedStrings.labels.dialog.exitWarning.title}
        portal={true}
        onPrimaryActionClick={() => handleClose(true)}
        primaryActionName={
          sharedStrings.labels.dialog.exitWarning.primaryAction
        }
        onSecondaryActionClick={() => setExitPromptOpened(false)}
        secondaryActionName={
          sharedStrings.labels.dialog.exitWarning.secondaryAction
        }
        negative
      >
        {sharedStrings.labels.dialog.exitWarning.body}
      </Dialog>
    </Drawer>
  );
}

export default EditContactsDrawer;
